import { FC, MouseEvent, useState } from "react"
import { Check, Copy } from "lucide-react"
import { useAppDispatch } from "../../hooks"
import { pushToast } from "../../../redux/slices/notifications"
import { copyToClipboard } from "../../utils"
import { cn } from "../../utils/classnames"

interface Props {
  text: string
  ariaLabel?: string
  /**
   * When set, renders the labelled button variant (bordered, icon + text)
   * instead of the bare icon button.
   */
  label?: string
  className?: string
  /** Show the copied value as a second line in the success toast. */
  showCopiedValue?: boolean
}

const CopyTextButton: FC<Props> = ({
  text,
  ariaLabel = "Copy to clipboard",
  label,
  className,
  showCopiedValue = false
}) => {
  const dispatch = useAppDispatch()
  const [copied, setCopied] = useState(false)

  const handleCopy = async (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    e.stopPropagation()

    try {
      await copyToClipboard(text)
      setCopied(true)
      dispatch(
        pushToast({
          msg: showCopiedValue ? (
            <>
              Copied to clipboard
              <span className="mt-0.5 block truncate text-xs font-normal text-neutral-400">
                {text}
              </span>
            </>
          ) : (
            "Copied to clipboard"
          ),
          theme: "success",
          timeout: 2000
        })
      )
      setTimeout(() => setCopied(false), 2000)
    } catch {
      dispatch(
        pushToast({
          msg: "Failed to copy to clipboard",
          theme: "danger",
          timeout: 2000
        })
      )
    }
  }

  const Icon = copied ? Check : Copy

  return (
    <button
      type="button"
      onClick={handleCopy}
      aria-label={ariaLabel}
      className={cn(
        label
          ? "inline-flex items-center gap-2 rounded border-[1.5px] border-primary-400 px-4 py-2.5 text-sm font-medium text-primary-400 transition-colors hover:bg-primary-50"
          : "relative z-10 rounded p-1 text-neutral-500 transition-colors hover:bg-neutral-100 hover:text-neutral-700",
        className
      )}
    >
      <Icon size={14} />
      {label && <span>{copied ? "Copied!" : label}</span>}
    </button>
  )
}

export default CopyTextButton
